import React, { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  DollarSign,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  ExternalLink,
  Settings,
  History,
  Info
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface QuickBooksSettings {
  client_id: string;
  client_secret: string;
  realm_id: string;
  is_connected: boolean;
  last_sync_time: string | null;
  sync_enabled: boolean;
  auto_sync_enabled: boolean;
  sync_inventory: boolean;
  sync_sales_orders: boolean;
  sync_purchase_orders: boolean;
  sync_customers_suppliers: boolean;
}

interface SyncLog {
  id: string;
  sync_type: string;
  status: string;
  items_synced: number;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
}

const QuickBooksSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<QuickBooksSettings>({
    client_id: '',
    client_secret: '',
    realm_id: '',
    is_connected: false,
    last_sync_time: null,
    sync_enabled: false,
    auto_sync_enabled: false,
    sync_inventory: true,
    sync_sales_orders: true,
    sync_purchase_orders: true,
    sync_customers_suppliers: true,
  });
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchSyncLogs();
  }, [user]);

  const fetchSettings = async () => {
    try {
      if (!user) return;

      const { data, error } = await supabase
        .from('quickbooks_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings({
          client_id: data.client_id || '',
          client_secret: data.client_secret || '',
          realm_id: data.realm_id || '',
          is_connected: data.is_connected || false,
          last_sync_time: data.last_sync_time,
          sync_enabled: data.sync_enabled || false,
          auto_sync_enabled: data.auto_sync_enabled || false,
          sync_inventory: data.sync_inventory !== false,
          sync_sales_orders: data.sync_sales_orders !== false,
          sync_purchase_orders: data.sync_purchase_orders !== false,
          sync_customers_suppliers: data.sync_customers_suppliers !== false,
        });
      }
    } catch (error) {
      console.error('Error fetching QuickBooks settings:', error);
    }
  };

  const fetchSyncLogs = async () => {
    try {
      if (!user) return;

      const { data, error } = await supabase
        .from('quickbooks_sync_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      if (data) {
        setSyncLogs(data);
      }
    } catch (error) {
      console.error('Error fetching sync logs:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (!user) return;

      const { error } = await supabase
        .from('quickbooks_settings')
        .upsert({
          user_id: user.id,
          client_id: settings.client_id,
          client_secret: settings.client_secret,
          realm_id: settings.realm_id,
          sync_enabled: settings.sync_enabled,
          auto_sync_enabled: settings.auto_sync_enabled,
          sync_inventory: settings.sync_inventory,
          sync_sales_orders: settings.sync_sales_orders,
          sync_purchase_orders: settings.sync_purchase_orders,
          sync_customers_suppliers: settings.sync_customers_suppliers,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setMessage('QuickBooks settings saved successfully!');
      await fetchSettings();
    } catch (error) {
      console.error('Error saving QuickBooks settings:', error);
      setMessage('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // TODO: Implement actual OAuth flow with QuickBooks API
  const handleConnect = async () => {
    setMessage('QuickBooks OAuth connection coming soon. Please save your credentials for now.');
    // Future implementation: Redirect to QuickBooks OAuth authorization URL
  };

  // TODO: Implement disconnect functionality
  const handleDisconnect = async () => {
    setLoading(true);
    try {
      if (!user) return;

      const { error } = await supabase
        .from('quickbooks_settings')
        .update({
          is_connected: false,
          access_token: null,
          refresh_token: null,
          token_expiry: null,
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setMessage('Disconnected from QuickBooks');
      await fetchSettings();
    } catch (error) {
      console.error('Error disconnecting:', error);
      setMessage('Failed to disconnect');
    } finally {
      setLoading(false);
    }
  };

  // TODO: Implement manual sync functionality
  const handleManualSync = async (syncType: string = 'full') => {
    setMessage(`Manual sync (${syncType}) coming soon. This will sync your data with QuickBooks.`);
    // Future implementation: Call QuickBooks API to sync data
  };

  // TODO: Implement connection test
  const handleTestConnection = async () => {
    setMessage('Connection test coming soon. This will verify your QuickBooks credentials.');
    // Future implementation: Test API connection with provided credentials
  };

  const getStatusBadge = () => {
    if (settings.is_connected) {
      return (
        <Badge variant="success" className="flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Connected
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        <XCircle className="h-3 w-3" />
        Not Connected
      </Badge>
    );
  };

  const getSyncStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'partial':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-700" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                QuickBooks Integration
                <Badge variant="info" className="text-xs">Coming Soon</Badge>
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Connect your QuickBooks Online account for seamless accounting
              </p>
            </div>
          </div>
          {getStatusBadge()}
        </div>

        {/* Connection Status Info */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                About QuickBooks Integration
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                This integration allows you to sync inventory, sales orders, purchase orders, and customer data
                between this system and QuickBooks Online in real-time. You can configure what data to sync and
                when to sync it automatically or manually.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* API Credentials Section */}
          <div>
            <button
              type="button"
              onClick={() => setShowCredentials(!showCredentials)}
              className="flex items-center justify-between w-full p-3 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                <span className="font-semibold text-gray-900 dark:text-white">API Credentials</span>
              </div>
              {showCredentials ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>

            {showCredentials && (
              <div className="mt-4 space-y-4 pl-4">
                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-800 dark:text-white">
                    Client ID
                  </label>
                  <Input
                    name="client_id"
                    type="password"
                    value={settings.client_id}
                    onChange={handleChange}
                    placeholder="Enter your QuickBooks Client ID"
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Get this from your QuickBooks Developer account
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-800 dark:text-white">
                    Client Secret
                  </label>
                  <Input
                    name="client_secret"
                    type="password"
                    value={settings.client_secret}
                    onChange={handleChange}
                    placeholder="Enter your QuickBooks Client Secret"
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Keep this secret secure and never share it
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-800 dark:text-white">
                    Company ID (Realm ID)
                  </label>
                  <Input
                    name="realm_id"
                    value={settings.realm_id}
                    onChange={handleChange}
                    placeholder="Enter your QuickBooks Company/Realm ID"
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Found in your QuickBooks company settings
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={handleTestConnection}
                    variant="secondary"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Test Connection
                  </Button>
                  {!settings.is_connected ? (
                    <Button
                      type="button"
                      onClick={handleConnect}
                      className="flex items-center gap-2"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Connect to QuickBooks
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleDisconnect}
                      variant="secondary"
                      className="flex items-center gap-2"
                    >
                      <XCircle className="h-4 w-4" />
                      Disconnect
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sync Preferences */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Sync Preferences
            </h3>
            <div className="space-y-3 pl-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="sync_enabled"
                  checked={settings.sync_enabled}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm font-medium text-gray-800 dark:text-white">
                  Enable QuickBooks Sync
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="auto_sync_enabled"
                  checked={settings.auto_sync_enabled}
                  onChange={handleChange}
                  disabled={!settings.sync_enabled}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label className="ml-2 block text-sm font-medium text-gray-800 dark:text-white">
                  Real-time Auto Sync
                  <span className="text-xs text-gray-600 dark:text-gray-400 ml-2">
                    (syncs immediately when changes occur)
                  </span>
                </label>
              </div>

              <div className="ml-6 space-y-2 mt-3">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  What to Sync
                </p>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="sync_inventory"
                    checked={settings.sync_inventory}
                    onChange={handleChange}
                    disabled={!settings.sync_enabled}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Inventory Items
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="sync_sales_orders"
                    checked={settings.sync_sales_orders}
                    onChange={handleChange}
                    disabled={!settings.sync_enabled}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Sales Orders
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="sync_purchase_orders"
                    checked={settings.sync_purchase_orders}
                    onChange={handleChange}
                    disabled={!settings.sync_enabled}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Purchase Orders
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="sync_customers_suppliers"
                    checked={settings.sync_customers_suppliers}
                    onChange={handleChange}
                    disabled={!settings.sync_enabled}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Customers and Suppliers
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Manual Sync Controls */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Manual Sync
            </h3>
            <div className="flex flex-wrap gap-2 pl-4">
              <Button
                type="button"
                onClick={() => handleManualSync('full')}
                variant="secondary"
                disabled={!settings.sync_enabled}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Sync All
              </Button>
              <Button
                type="button"
                onClick={() => handleManualSync('inventory')}
                variant="secondary"
                disabled={!settings.sync_enabled || !settings.sync_inventory}
                className="flex items-center gap-2"
              >
                Inventory Only
              </Button>
              <Button
                type="button"
                onClick={() => handleManualSync('orders')}
                variant="secondary"
                disabled={!settings.sync_enabled}
                className="flex items-center gap-2"
              >
                Orders Only
              </Button>
            </div>

            {settings.last_sync_time && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 pl-4">
                Last synced: {formatDistanceToNow(new Date(settings.last_sync_time), { addSuffix: true })}
              </p>
            )}
          </div>

          {/* Sync History */}
          <div>
            <button
              type="button"
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center justify-between w-full p-3 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-2">
                <History className="h-5 w-5" />
                <span className="font-semibold text-gray-900 dark:text-white">Sync History</span>
                {syncLogs.length > 0 && (
                  <Badge variant="secondary">{syncLogs.length}</Badge>
                )}
              </div>
              {showHistory ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>

            {showHistory && (
              <div className="mt-4 space-y-2 pl-4">
                {syncLogs.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">No sync history yet</p>
                ) : (
                  syncLogs.map((log) => (
                    <div
                      key={log.id}
                      className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {getSyncStatusIcon(log.status)}
                          <div>
                            <p className="font-medium text-sm text-gray-900 dark:text-white">
                              {log.sync_type.charAt(0).toUpperCase() + log.sync_type.slice(1)} Sync
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              {formatDistanceToNow(new Date(log.started_at), { addSuffix: true })}
                            </p>
                            {log.items_synced > 0 && (
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                {log.items_synced} items synced
                              </p>
                            )}
                            {log.error_message && (
                              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                {log.error_message}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge
                          variant={
                            log.status === 'success'
                              ? 'success'
                              : log.status === 'failed'
                              ? 'error'
                              : 'warning'
                          }
                        >
                          {log.status}
                        </Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Help Section */}
          <div>
            <button
              type="button"
              onClick={() => setShowHelp(!showHelp)}
              className="flex items-center justify-between w-full p-3 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                <span className="font-semibold text-gray-900 dark:text-white">Setup Instructions</span>
              </div>
              {showHelp ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>

            {showHelp && (
              <div className="mt-4 space-y-4 pl-4 text-sm text-gray-700 dark:text-gray-300">
                <div>
                  <h4 className="font-semibold mb-2">How to get your QuickBooks API credentials:</h4>
                  <ol className="list-decimal list-inside space-y-2">
                    <li>
                      Visit the{' '}
                      <a
                        href="https://developer.intuit.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                      >
                        Intuit Developer Portal
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </li>
                    <li>Sign in with your QuickBooks account</li>
                    <li>Create a new app or select an existing one</li>
                    <li>Navigate to the "Keys & OAuth" section</li>
                    <li>Copy your Client ID and Client Secret</li>
                    <li>Find your Company ID in QuickBooks under Company Settings</li>
                  </ol>
                </div>

                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <h4 className="font-semibold mb-1 text-yellow-900 dark:text-yellow-100">
                    Important Security Note
                  </h4>
                  <p className="text-xs text-yellow-800 dark:text-yellow-200">
                    Your API credentials are encrypted and stored securely. Never share your Client Secret
                    with anyone. The integration will only sync data you explicitly enable in the preferences above.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Frequently Asked Questions:</h4>
                  <div className="space-y-2">
                    <div>
                      <p className="font-medium">What data gets synced?</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Only the data types you enable in the sync preferences will be synchronized
                        between this system and QuickBooks.
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">How often does auto-sync run?</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        When real-time auto-sync is enabled, changes are synced immediately.
                        Otherwise, you can trigger manual syncs at any time.
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Can I disconnect at any time?</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Yes, you can disconnect from QuickBooks at any time using the disconnect button.
                        Your data will remain intact in both systems.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>

          {message && (
            <p className={`text-sm ${message.includes('Failed') || message.includes('failed') ? 'text-red-600' : 'text-green-600'}`}>
              {message}
            </p>
          )}
        </form>
      </div>
    </Card>
  );
};

export default QuickBooksSettings;
